import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../../services/api';
import { getParcoursActif } from '../../services/parcoursActif';
import { PenLine, Loader2, Lock, ChevronRight, Check, X, Target, RotateCcw } from 'lucide-react';
import QrcVerdictView from './QrcVerdictView';
import { newRequestKey, qrcForceCredits } from './QrcPlayer';

/**
 * Audit QRC d'un scope : une question rédigée par sujet, en phase SÉPARÉE des
 * QCM, puis un BILAN PAR SUJET (ce qu'il faut travailler). Réutilise le juge
 * (qrc-grade.php) et le rendu verdict partagé.
 */
export default function QrcAudit({ scopeId, scopeColor }) {
    const keyRef = useRef(null); // clé d'idempotence de la soumission en cours
    const [items, setItems] = useState(null); // null = chargement
    const [phase, setPhase] = useState('idle'); // idle | running | done
    const [idx, setIdx] = useState(0);
    const [answer, setAnswer] = useState('');
    const [grading, setGrading] = useState(false);
    const [current, setCurrent] = useState(null); // verdict de l'item courant
    const [results, setResults] = useState({}); // qrc_id -> { subject, matiere, passed, score, state }
    const [err, setErr] = useState(null);

    const parcours = getParcoursActif().slug;
    const load = useCallback(() => {
        // Spec crédits §8 : un admin peut forcer le mode crédits (drapeau local, sans effet sinon).
        return api.get(`/qcm/qrc-audit.php?scope_id=${scopeId}${qrcForceCredits() ? '&policy=credits' : ''}`)
            .then(({ data }) => setItems(data.data.items || []))
            .catch(() => setItems([]));
        // parcours : l'intercepteur joint le parcours actif ; on recharge quand il change.
    }, [scopeId, parcours]);
    useEffect(() => { load(); }, [load]);

    // Recharge l'état serveur avant de repartir (sinon on rejoue une liste
    // périmée → refus 403 du gate de rejeu sur les QRC déjà tentées).
    const restart = async () => { await load(); setPhase('idle'); };

    if (items === null || items.length === 0) return null;

    const playable = items.filter(i => i.accessible && (!i.attempted || i.replayable));

    const start = () => {
        // Pré-remplit le bilan avec les sujets déjà tranchés (verrouillés ou déjà tentés non rejouables)
        const seed = {};
        items.forEach(i => {
            if (!i.accessible) seed[i.qrc_id] = { subject: i.sous_sujet_label, matiere: i.matiere, state: 'locked' };
            else if (i.attempted && !i.replayable) seed[i.qrc_id] = { subject: i.sous_sujet_label, matiere: i.matiere, passed: i.last_passed, score: i.last_score, state: 'kept' };
        });
        setResults(seed);
        setPhase(playable.length ? 'running' : 'done');
        setIdx(0); setAnswer(''); setCurrent(null); setErr(null);
    };

    const submit = async () => {
        const item = playable[idx];
        if (!answer.trim()) return;
        setGrading(true); setErr(null);
        try {
            // Scope explicite (§2.1) + clé d'idempotence (§4.1) : une relance de la
            // même réponse (coupure réseau) réutilise la clé et ne débite pas deux fois.
            const requestKey = keyRef.current || newRequestKey();
            keyRef.current = requestKey;
            const { data } = await api.post('/qcm/qrc-grade.php', {
                qrc_question_id: item.qrc_id, answer, origin: 'audit',
                scope_id: scopeId, request_key: requestKey,
                ...(qrcForceCredits() ? { policy: 'credits' } : {}),
            }, { timeout: 240000 });
            if (data.data?.replay === 'pending') {
                setErr('Correction en cours — réessaie dans quelques secondes (aucun second débit).');
                setGrading(false);
                return;
            }
            if (data.data?.replay === 'deleted') {
                keyRef.current = null;
                setErr(data.message || 'Le résultat de cette correction a été effacé.');
                setGrading(false);
                return;
            }
            keyRef.current = null;
            const v = data.data.verdict;
            setCurrent(v);
            setResults(prev => ({
                ...prev,
                [item.qrc_id]: {
                    subject: item.sous_sujet_label, matiere: item.matiere,
                    passed: v.passed === 1 || v.passed === true, score: v.score_core_pct ?? v.score_pct, state: 'done',
                },
            }));
        } catch (e) {
            const status = e.response?.status;
            // 409 expirée ou 402 sans crédit : la prochaine soumission repart avec une clé neuve.
            if (status === 409 || status === 402) keyRef.current = null;
            setErr((status ? `${status} — ` : '') + (e.response?.data?.message || 'Correction indisponible'));
        }
        setGrading(false);
    };

    const next = () => {
        if (idx + 1 < playable.length) { setIdx(idx + 1); setAnswer(''); setCurrent(null); setErr(null); }
        else setPhase('done');
    };

    const accent = scopeColor || 'var(--primary)';

    // ---- Écran d'intro ----
    if (phase === 'idle') {
        const acquired = items.filter(i => i.attempted && i.last_passed).length;
        const toWork = items.filter(i => i.attempted && !i.last_passed).length;
        const lockedCount = items.filter(i => !i.accessible).length;
        const neverTried = items.every(i => !i.attempted);
        return (
            <div className="qrca" style={{ '--qrcp-accent': accent }}>
                <div className="qrca-intro">
                    <span className="qrca-intro-ic"><PenLine size={20} /></span>
                    <div className="qrca-intro-txt">
                        <strong>
                            Audit rédigé — {items.length} sujet{items.length > 1 ? 's' : ''}
                            {neverTried && <span className="qrca-intro-new">Nouveau</span>}
                        </strong>
                        <p>Une question à réponse rédigée par sujet, corrigée par IA. À la fin, un bilan te dit précisément quoi travailler.</p>
                        {!neverTried && (
                            <div className="qrca-intro-stats">
                                {acquired > 0 && <span className="qrca-bilan-tag ok"><Check size={12} /> {acquired} acquis</span>}
                                {toWork > 0 && <span className="qrca-bilan-tag ko"><X size={12} /> {toWork} à travailler</span>}
                                {playable.length > 0 && <span className="qrca-bilan-tag skip">{playable.length} à faire</span>}
                                {lockedCount > 0 && <span className="qrca-bilan-tag lock"><Lock size={11} /> {lockedCount} verrouillé{lockedCount > 1 ? 's' : ''}</span>}
                            </div>
                        )}
                    </div>
                    <button className="btn btn-primary btn-sm" onClick={start}>
                        {playable.length ? 'Démarrer l\'audit' : 'Voir mon bilan'} <ChevronRight size={15} />
                    </button>
                </div>
            </div>
        );
    }

    // ---- Bilan final ----
    if (phase === 'done') {
        const rows = items.map(i => results[i.qrc_id] || { subject: i.sous_sujet_label, matiere: i.matiere, state: 'skipped' });
        const graded = rows.filter(r => r.state === 'done' || r.state === 'kept');
        const weakest = [...graded].filter(r => !r.passed).sort((a, b) => (a.score ?? 0) - (b.score ?? 0))[0];
        return (
            <div className="qrca" style={{ '--qrcp-accent': accent }}>
                <div className="qrca-bilan">
                    <div className="qrca-bilan-head"><Target size={16} /> Bilan par sujet</div>
                    <ul className="qrca-bilan-list">
                        {rows.map((r, i) => (
                            <li key={i} className="qrca-bilan-row">
                                <span className="qrca-bilan-sujet">
                                    {r.subject}
                                    {r.matiere && <small>{r.matiere}</small>}
                                </span>
                                {r.state === 'locked' ? (
                                    <span className="qrca-bilan-tag lock"><Lock size={11} /> Abonnement</span>
                                ) : r.state === 'skipped' ? (
                                    <span className="qrca-bilan-tag skip">Non fait</span>
                                ) : r.passed ? (
                                    <span className="qrca-bilan-tag ok"><Check size={12} /> Acquis · {r.score} %</span>
                                ) : (
                                    <span className="qrca-bilan-tag ko"><X size={12} /> À travailler · {r.score} %</span>
                                )}
                            </li>
                        ))}
                    </ul>
                    <p className="qrca-bilan-next">
                        {weakest
                            ? <>Priorité : <strong>{weakest.subject}</strong> — enchaîne avec ses QCM, puis reviens re-tester.</>
                            : graded.length
                                ? 'Beau travail — tous les sujets testés sont acquis.'
                                : 'Aucun sujet testé pour l\'instant.'}
                    </p>
                    <button className="qrca-restart" onClick={restart}><RotateCcw size={13} /> Refaire l'audit</button>
                </div>
            </div>
        );
    }

    // ---- En cours ----
    const item = playable[idx];
    return (
        <div className="qrca" style={{ '--qrcp-accent': accent }}>
            <div className="qrca-run">
                <div className="qrca-run-head">
                    <span className="qrca-run-step">Sujet {idx + 1} / {playable.length}</span>
                    <span className="qrca-run-sujet">{item.sous_sujet_label}</span>
                </div>
                <p className="qrca-run-enonce">{item.enonce}</p>

                {!current ? (
                    <div className="qrcp-body" style={{ padding: 0 }}>
                        <textarea
                            rows={5} value={answer} maxLength={5000}
                            onChange={e => setAnswer(e.target.value)}
                            placeholder="Rédige ta réponse en quelques phrases…"
                        />
                        {err && <p className="qrcp-err">{err}</p>}
                        <button className="btn btn-primary btn-sm" onClick={submit} disabled={grading || !answer.trim()}>
                            {grading ? <><Loader2 size={14} className="spin" /> Correction…</> : 'Corriger ma réponse'}
                        </button>
                    </div>
                ) : (
                    <>
                        <QrcVerdictView verdict={current} />
                        <button className="btn btn-primary btn-sm qrca-next" onClick={next}>
                            {idx + 1 < playable.length ? <>Sujet suivant <ChevronRight size={15} /></> : <>Voir mon bilan <ChevronRight size={15} /></>}
                        </button>
                    </>
                )}
            </div>
        </div>
    );
}
